import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasActiveInternalNetworkDiscoveryRun } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';
import { internalNetworkDiscoveryApi } from '../../clients/tester-client/internal-network-discovery-api';
import { INTERNAL_NETWORK_GRAPH_KEY } from './useInternalNetworkGraph';
import {
  INTERNAL_NETWORK_DISCOVERY_RUNS_KEY,
  INTERNAL_NETWORK_OWNED_PRINCIPALS_KEY,
  INTERNAL_NETWORK_SIGNALS_TREND_KEY,
} from './useInternalNetworkOverview';
import {
  INTERNAL_NETWORK_CROWN_JEWELS_KEY,
  INTERNAL_NETWORK_SCENARIOS_KEY,
} from './useInternalNetworkScenarios';
import { INTERNAL_NETWORK_STATS_KEY } from './useInternalNetworkStats';
import {
  INTERNAL_NETWORK_CREDENTIALS_KEY,
  INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY,
} from './useSurfaceSettings';

export const INTERNAL_NETWORK_DISCOVERY_READINESS_KEY =
  'internal-network-discovery-readiness';

const ACTIVE_DISCOVERY_RUNS_LIMIT = 50;
const ACTIVE_DISCOVERY_POLL_MS = 5_000;
const DISCOVERY_READINESS_POLL_MS = 30_000;

export function useActiveInternalNetworkDiscovery(networkId: string | null) {
  const query = useQuery({
    queryKey: [INTERNAL_NETWORK_DISCOVERY_RUNS_KEY, networkId, 'active'],
    queryFn: () =>
      terraProxyApiClient.listInternalNetworkDiscoveryRuns({
        networkId: networkId ?? '',
        skip: 0,
        limit: ACTIVE_DISCOVERY_RUNS_LIMIT,
      }),
    enabled: Boolean(networkId),
    refetchInterval: (state) =>
      hasActiveInternalNetworkDiscoveryRun(state.state.data?.data ?? [])
        ? ACTIVE_DISCOVERY_POLL_MS
        : false,
  });

  return {
    isDiscoveryActive: hasActiveInternalNetworkDiscoveryRun(
      query.data?.data ?? [],
    ),
    isLoading: query.isLoading,
  };
}

export function useInternalNetworkDiscoveryReadiness(networkId: string | null) {
  return useQuery({
    queryKey: [INTERNAL_NETWORK_DISCOVERY_READINESS_KEY, networkId],
    queryFn: () =>
      internalNetworkDiscoveryApi.getInternalNetworkDiscoveryReadiness(
        networkId ?? '',
      ),
    enabled: Boolean(networkId),
    refetchInterval: DISCOVERY_READINESS_POLL_MS,
  });
}

export function useResetInternalNetworkDiscovery(networkId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!networkId) {
        throw new Error('Network ID is required');
      }

      return internalNetworkDiscoveryApi.resetInternalNetworkDiscoveryData(
        networkId,
      );
    },
    onSuccess: () =>
      Promise.all(
        [
          INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY,
          INTERNAL_NETWORK_DISCOVERY_RUNS_KEY,
          INTERNAL_NETWORK_STATS_KEY,
          INTERNAL_NETWORK_SIGNALS_TREND_KEY,
          INTERNAL_NETWORK_GRAPH_KEY,
          INTERNAL_NETWORK_CROWN_JEWELS_KEY,
          INTERNAL_NETWORK_OWNED_PRINCIPALS_KEY,
          INTERNAL_NETWORK_CREDENTIALS_KEY,
          INTERNAL_NETWORK_SCENARIOS_KEY,
          INTERNAL_NETWORK_DISCOVERY_READINESS_KEY,
        ].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key, networkId] }),
        ),
      ),
  });
}
